<template>
  <el-dialog
    v-model="visible"
    :title="templateName || '协议签署'"
    width="820px"
    top="6vh"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    destroy-on-close
    class="agreement-sign-dialog"
    @opened="onDialogOpened"
    @closed="onDialogClosed"
  >
    <div class="agreement-body" v-html="previewBody" />

    <div class="sign-area">
      <div class="field">
        <div class="field-head">
          <label>手写签名</label>
          <span class="tip">手写签名后将自动同步到协议正文</span>
        </div>
        <div ref="wrapRef" class="canvas-wrap" :class="{ signed }">
          <canvas
            ref="canvasRef"
            @mousedown.prevent="start"
            @mousemove.prevent="move"
            @mouseup.prevent="stop"
            @mouseleave="stop"
            @touchstart.prevent="start"
            @touchmove.prevent="move"
            @touchend.prevent="stop"
          />
          <span v-show="!signed" class="hint">请在此区域手写签名</span>
          <el-button class="clear-btn" link type="primary" @click.stop="clearSign">清除</el-button>
        </div>
      </div>
      <el-checkbox v-model="agreed">本人已完整阅读并理解协议全部内容，自愿签署并承担相应责任。</el-checkbox>
    </div>

    <template #footer>
      <el-button :loading="submitting" type="primary" :disabled="!canConfirm" @click="onConfirm">
        确认签署
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { submitAgreementSign } from '@/api/agreement'
import { getUserShowName } from '@/utils/storage'

const props = defineProps<{
  modelValue: boolean
  templateId?: string | number
  templateName?: string
  templateBody?: string
  /** 活体认证通过后的 CertifyId */
  certifyId?: string
  /** PC 扫码 ticketId */
  ticketId?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'signed'): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const wrapRef = ref<HTMLElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const agreed = ref(false)
const signed = ref(false)
const drawing = ref(false)
const submitting = ref(false)
const signatureDataUrl = ref('')
const last = ref({ x: 0, y: 0 })
let ctx: CanvasRenderingContext2D | null = null
let resizeObserver: ResizeObserver | null = null

const canConfirm = computed(() => signed.value && agreed.value && !submitting.value)

const todayText = () => {
  const d = new Date()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

const resolveSignerName = () => {
  const name = (getUserShowName() || '').trim()
  return name || '学员'
}

const previewBody = computed(() => {
  let html = props.templateBody || ''
  const date = signed.value ? todayText() : ''
  const signImg = signatureDataUrl.value
    ? `<img class="inline-sign" src="${signatureDataUrl.value}" alt="手写签名" />`
    : ''

  const replaceCode = (code: string, value: string) => {
    html = html.split(`\${${code}}`).join(value)
    html = html.split(`$${code}`).join(value)
  }

  replaceCode('name', signImg)
  replaceCode('studentName', signImg)
  replaceCode('date', date)
  replaceCode('data', date)
  ;['name2', 'sign', 'signature', 'handSign', 'handsign'].forEach((code) => {
    replaceCode(code, signImg)
  })

  html = html.replace(/(姓名\s*[：:])\s*(?:<img[^>]*>|[^<\n]*)/g, `$1${signImg}`)
  html = html.replace(/(签署日期\s*[：:])\s*[^<\n]*/g, `$1${date}`)

  return html
})

const getPoint = (e: MouseEvent | TouchEvent) => {
  const canvas = canvasRef.value!
  const rect = canvas.getBoundingClientRect()
  const p = 'touches' in e ? e.touches[0] : (e as MouseEvent)
  const scaleX = canvas.width / Math.max(rect.width, 1)
  const scaleY = canvas.height / Math.max(rect.height, 1)
  return {
    x: (p.clientX - rect.left) * scaleX,
    y: (p.clientY - rect.top) * scaleY,
  }
}

const syncSignaturePreview = () => {
  const canvas = canvasRef.value
  if (!canvas || !signed.value) {
    signatureDataUrl.value = ''
    return
  }
  signatureDataUrl.value = canvas.toDataURL('image/png')
}

const ensureCanvas = () => {
  const canvas = canvasRef.value
  const wrap = wrapRef.value
  if (!canvas || !wrap) return false

  const rect = wrap.getBoundingClientRect()
  const cssW = Math.max(Math.floor(rect.width), 1)
  const cssH = Math.max(Math.floor(rect.height), 1)
  const dpr = window.devicePixelRatio || 1
  const nextW = Math.floor(cssW * dpr)
  const nextH = Math.floor(cssH * dpr)

  // 尺寸未变化且已有上下文时不重置，避免把手写内容清掉
  if (ctx && canvas.width === nextW && canvas.height === nextH) {
    return true
  }

  const old = signed.value && canvas.width > 0 ? canvas.toDataURL() : null
  canvas.width = nextW
  canvas.height = nextH
  canvas.style.width = `${cssW}px`
  canvas.style.height = `${cssH}px`

  ctx = canvas.getContext('2d')
  if (!ctx) return false
  ctx.lineWidth = 5.5 * dpr
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#173f78'

  if (old) {
    const img = new Image()
    img.onload = () => {
      ctx?.drawImage(img, 0, 0, nextW, nextH)
      syncSignaturePreview()
    }
    img.src = old
  }
  return true
}

const start = (e: MouseEvent | TouchEvent) => {
  if (!ensureCanvas() || !ctx) return
  drawing.value = true
  last.value = getPoint(e)
  // 落笔点也画一下，避免只点一下看不到
  ctx.beginPath()
  ctx.fillStyle = '#173f78'
  ctx.arc(last.value.x, last.value.y, Math.max(ctx.lineWidth / 2, 2.5 * (window.devicePixelRatio || 1)), 0, Math.PI * 2)
  ctx.fill()
}

const move = (e: MouseEvent | TouchEvent) => {
  if (!drawing.value) return
  if (!ctx && !ensureCanvas()) return
  if (!ctx) return
  const p = getPoint(e)
  ctx.beginPath()
  ctx.moveTo(last.value.x, last.value.y)
  ctx.lineTo(p.x, p.y)
  ctx.stroke()
  last.value = p
  signed.value = true
}

const stop = () => {
  if (!drawing.value) return
  drawing.value = false
  if (signed.value) syncSignaturePreview()
}

const clearSign = () => {
  if (!ensureCanvas() || !ctx || !canvasRef.value) return
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  signed.value = false
  signatureDataUrl.value = ''
}

const resetForm = () => {
  agreed.value = false
  signed.value = false
  drawing.value = false
  submitting.value = false
  signatureDataUrl.value = ''
  ctx = null
}

const bindResizeObserver = () => {
  unbindResizeObserver()
  if (!wrapRef.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => {
    ensureCanvas()
  })
  resizeObserver.observe(wrapRef.value)
}

const unbindResizeObserver = () => {
  resizeObserver?.disconnect()
  resizeObserver = null
}

const onDialogOpened = async () => {
  resetForm()
  await nextTick()
  // 弹窗动画结束后再初始化，避免宽高为 0
  setTimeout(() => {
    ensureCanvas()
    bindResizeObserver()
  }, 50)
}

const onDialogClosed = () => {
  unbindResizeObserver()
  ctx = null
}

onBeforeUnmount(() => {
  unbindResizeObserver()
})

const onConfirm = async () => {
  if (!canConfirm.value || !props.templateId) return
  const canvas = canvasRef.value
  if (!canvas) return
  submitting.value = true
  try {
    await submitAgreementSign({
      templateId: props.templateId,
      signerName: resolveSignerName(),
      signatureImg: canvas.toDataURL('image/png'),
      certifyId: props.certifyId || undefined,
      ticketId: props.ticketId || undefined,
    })
    ElMessage.success('签署成功')
    visible.value = false
    emit('signed')
  } catch (e) {
    console.error('协议签署失败', e)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.agreement-body {
  max-height: 42vh;
  overflow: auto;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafbfc;
  line-height: 1.75;
  color: #303133;
  font-size: 14px;

  :deep(p) {
    margin: 0 0 10px;
  }

  :deep(.inline-sign) {
    display: inline-block;
    height: 36px;
    width: auto;
    max-width: 160px;
    vertical-align: middle;
    margin-left: 6px;
  }
}

.sign-area {
  margin-top: 16px;
}

.field {
  margin-bottom: 12px;

  .field-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  label {
    color: #606266;
    font-weight: 600;
    font-size: 13px;
  }

  .tip {
    color: #909399;
    font-size: 12px;
  }
}

.canvas-wrap {
  position: relative;
  height: 140px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fbfcfe;
  overflow: hidden;
  user-select: none;

  &.signed {
    border-color: #409eff;
  }

  canvas {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: crosshair;
  }
}

.hint {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
  pointer-events: none;
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 6px;
  z-index: 2;
}
</style>
